import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import React, { useContext, useEffect } from "react";
import { AuthContext } from "../../context/auth";
import Colors from "../../enums/colors";
import { GlobalLoaderContext } from "../../context/global-loader";
import ApiUri from "../../api/api-uri";
import API from "../../api/api";
import BackButton from "../../components/BackButton";
import { TranslationsContext } from "../../context/translations";
import { InformationContext } from "../../context/information";

const BaseComponentLogin = ({
  children,
  backButton = false,
  headerSection = null,
  isLoading = false,
}: {
  children?: React.ReactNode;
  backButton?: boolean;
  headerSection?: React.ReactNode;
  isLoading?: boolean;
}) => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const { startGlobalLoader, stopGlobalLoader } =
    useContext(GlobalLoaderContext);
  const {
    setTranslations,
    hasFetchedTranslations,
    setHasFetchedTranslations,
  } = useContext(TranslationsContext);

  const { information } = useContext(InformationContext);

  useEffect(() => {
    if (!hasFetchedTranslations) {
      const locale = navigator.language;
      const language = locale.split("-")[0];
      API.get(ApiUri.PUBLIC + ApiUri.TRANSLATIONS, {
        params: { locale: language },
      }).then(
        (response) => {
          setHasFetchedTranslations(true);
          setTranslations(response.data.data);
        },
        () => {},
      );
    }
  }, []);

  const cardWidth = {
    xs: "100%",
    sm: "520px",
    md: "520px",
    lg: "520px",
    xl: "520px",
  };

  return (
    <Stack
      alignItems="center"
      sx={{
        minHeight: "100vh",
        width: "100vw",
        background: "linear-gradient(145deg, #0d1a26 0%, #152338 50%, #0b1821 100%)",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-15%",
          right: "5%",
          width: "500px",
          height: "500px",
          background: "radial-gradient(circle, rgba(51,153,254,0.07) 0%, transparent 65%)",
          pointerEvents: "none",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "0%",
          left: "-5%",
          width: "450px",
          height: "450px",
          background: "radial-gradient(circle, rgba(51,153,254,0.05) 0%, transparent 65%)",
          pointerEvents: "none",
        },
      }}
      justifyContent="space-between"
    >
      {/* Decorative vinyl-ring circles */}
      {[55, 72, 90].map((size, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${-12 - i * 6}%`,
            top: "50%",
            transform: "translateY(-50%)",
            width: `${size}vh`,
            height: `${size}vh`,
            borderRadius: "50%",
            border: `1px solid rgba(51,153,254,${0.05 - i * 0.015})`,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      ))}

      {(children || headerSection || isLoading) && (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            py: { xs: 2, md: 4 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: cardWidth,
              borderRadius: "16px",
              boxShadow: "0 24px 64px rgba(0,0,0,0.5), 0 8px 24px rgba(0,0,0,0.3)",
              overflow: "hidden",
              backgroundColor: headerSection ? "#0d1a26" : Colors.white,
              position: "relative",
              minHeight: isLoading && !children && !headerSection ? "515px" : undefined,
            }}
          >
            {/* Dark branded header */}
            {headerSection && (
              <Box
                sx={{
                  background: "linear-gradient(135deg, #0d1a26 0%, #152338 100%)",
                  px: 4,
                  pt: backButton ? 3.5 : 4,
                  pb: 3,
                  textAlign: "center",
                  position: "relative",
                  borderBottom: "2px solid rgba(51,153,254,0.65)",
                }}
              >
                {backButton && (
                  <Box sx={{ position: "absolute", top: 10, left: 12 }}>
                    <BackButton />
                  </Box>
                )}
                {headerSection}
              </Box>
            )}

            {/* White form body */}
            <Stack
              sx={{ backgroundColor: Colors.white }}
              padding={4}
              position="relative"
            >
              {!headerSection && backButton && (
                <Box position="absolute" paddingTop={2} top={2}>
                  <BackButton />
                </Box>
              )}
              <Stack
                alignItems="center"
                marginTop={!headerSection && backButton ? "40px" : 0}
              >
                {children}
              </Stack>
            </Stack>

            {isLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(13,26,38,0.55)",
                  backdropFilter: "blur(2px)",
                  borderRadius: "16px",
                }}
              >
                <CircularProgress size={48} sx={{ color: "#3399FE" }} />
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* Footer */}
      <Stack
        paddingBottom={3}
        sx={{ position: "relative", zIndex: 1 }}
        alignItems="center"
      >
        {information &&
          (information?.white_label ? (
            <Typography
              fontSize={11}
              sx={{ color: "rgba(255,255,255,0.3)", letterSpacing: "0.2px" }}
            >
              <a
                style={{
                  color: "rgba(51,153,254,0.5)",
                  textDecoration: "none",
                }}
                href={information?.web}
                target="_blank"
              >
                {information?.app_name}
              </a>
            </Typography>
          ) : (
            <Typography
              fontSize={11}
              sx={{ color: "rgba(255,255,255,0.3)", letterSpacing: "0.2px" }}
            >
              <a
                style={{
                  color: "rgba(51,153,254,0.5)",
                  textDecoration: "none",
                }}
                href="https://www.myclientzone.com/"
                target="_blank"
              >
                MyClientZone v5.0
              </a>{" "}
              — Copyright © {currentYear} Nick Dunn Enterprises Ltd.
            </Typography>
          ))}
      </Stack>
    </Stack>
  );
};

export default BaseComponentLogin;
